// External Packages
import { Fragment, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiCheck } from "react-icons/fi";
import { AiOutlineClose } from "react-icons/ai";
import { MdRefresh } from "react-icons/md";
import { FaEdit } from "react-icons/fa";

// Internal Packages
import { DataContext } from "../../context/data-context";
import { AuthContext } from "../../context/auth-context";
import { fetchCustomerById, fetchSubscriptionPlans } from "../../api/fetch";
import { timestampToString } from "../../util/timestamp-to-date";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import Switch from "../UI/Switch";
import PaymentForm from "../UI/PaymentForm";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";

/** This component renders a form to add subscription to
 * a customer and invoice them.
 */

export default function AddSubscription() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warning, setWarning] = useState(false);
  const [edit, setEdit] = useState(false);

  // ==================================== FETCH CUSTOMER
  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const data = await fetchCustomerById(user, id);
      setCustomer(data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.error(err);
    }
  };

  // ==================================== FETCH SUBSCRIPTION PLANS
  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetchSubscriptionPlans(user);
      setPlans(response);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setWarning(err.message);
      console.error(err);
    }
  };

  useEffect(() => {
    if (id) fetchCustomer();
    fetchPlans();
  }, [id]);

  if (!id)
    return (
      <div className="container">
        <p className="text-stone-400">Customer ID Not Provided!</p>
      </div>
    );

  if (!loading && !customer)
    return (
      <div className="container">
        <p className="text-stone-400">Customer Not Found!</p>
      </div>
    );

  return (
    <div className="container">
      <Heading
        title="Manage Subscription"
        text="Subscriptions are auto-renewed on renewal date unless 
      end date is set or plan is manually deactivated."
      >
        {edit ? (
          <Fragment>
            <FiCheck
              onClick={() => document.getElementById("form").click()}
              className="icon"
            />
            <AiOutlineClose onClick={() => setEdit(false)} className="icon" />
          </Fragment>
        ) : (
          <Fragment>
            <MdRefresh onClick={fetchCustomer} className="text-2xl icon" />
            <FaEdit onClick={() => setEdit(true)} className="icon" />
          </Fragment>
        )}
      </Heading>
      {loading ? (
        <LoadingSpinnerWrapper />
      ) : (
        <div className="grid gap-8">
          <div className="info">
            <DetailPair label="Customer ID" value={customer.id} />
            <DetailPair
              label="Customer"
              value={`${customer.firstName} ${customer.lastName}`}
            />
            <SelectPair
              label="Plan"
              value={customer.plan?.name}
              options={plans.map((plan) => plan.name)}
              edit={edit}
            />
            <DetailPair
              label="Start Date"
              value={
                customer.plan?.start_date &&
                timestampToString(customer.plan?.start_date, "MM/dd/yyyy")
              }
              type="date"
              edit={edit}
            />
            <DetailPair
              label="Renewal Date"
              value={
                customer.plan?.renewal_date &&
                timestampToString(customer.plan?.renewal_date, "MM/dd/yyyy")
              }
              type="date"
              edit={edit}
            />
            <DetailPair
              label="End Date"
              value={
                customer.plan?.end_date &&
                timestampToString(customer.plan?.end_date, "MM/dd/yyyy")
              }
              type="date"
              edit={edit}
            />
            <div className="flex items-center space-x-4">
              <Switch
                label="Active"
                value={customer.plan?.active}
                edit={edit}
              />
            </div>
          </div>

          {edit && (
            <div className="info">
              <p className="text-xs text-stone-400 md:col-span-3">
                Customers can be charged for any changes to subscription plan.
                The amount is sent as an invoice. For no charges, simply leave
                the amount and description empty.
              </p>
              <div className="grid">
                <label className="text-sm">Charge Amount:</label>
                <input className="form-input" name="amount" type="number" />
              </div>
              <div className="grid md:col-span-2">
                <label className="text-sm">Description:</label>
                <input
                  className="form-input"
                  name="description"
                  placeholder="Description"
                  type="text"
                  required
                />
              </div>
              <button className="btn-primary">Submit</button>
              {!warning && <p className="warning">{warning}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
